export class ApiError extends Error {
  status: number;
  detail?: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const ENV_API_KEY = process.env.REACT_APP_API_KEY ?? '';
const TOKEN_KEY = 'civic_alert_access_token';
const AUTH_NOTICE_KEY = 'scoutly_auth_notice';
export const AUTH_SESSION_ENDED_EVENT = 'scoutly:auth-session-ended';
export const ACCOUNT_REMOVED_MESSAGE =
  'Your account has been removed from the workspace. Contact support if you believe this is a mistake.';

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAccessToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAccessToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function consumeAuthNotice(): string | null {
  try {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
    sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return notice;
  } catch {
    return null;
  }
}

function apiErrorCode(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object' || !("code" in detail)) return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function handleAuthenticationFailure(status: number, detail: unknown): void {
  if (status !== 401 || apiErrorCode(detail) !== 'account_removed' || !getAccessToken()) return;
  clearAccessToken();
  try {
    sessionStorage.setItem(AUTH_NOTICE_KEY, ACCOUNT_REMOVED_MESSAGE);
  } catch {
    /* storage can be unavailable in privacy-restricted browsers */
  }
  window.dispatchEvent(new Event(AUTH_SESSION_ENDED_EVENT));
}

/** @deprecated API key is for server automation only — use JWT login in the UI */
export function getApiKey(): string {
  return ENV_API_KEY;
}

/** @deprecated */
export function setApiKey(_key: string): void {
  /* no-op — browser auth uses JWT */
}

/** @deprecated */
export function clearApiKey(): void {
  clearAccessToken();
}

type RequestOptions = RequestInit & { skipAuth?: boolean };

function buildHeaders(options: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (!options.skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else if (ENV_API_KEY) {
      headers['X-API-Key'] = ENV_API_KEY;
    }
  }
  return headers;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth: _skip, ...fetchOptions } = options;
  const headers = buildHeaders(options);

  const res = await fetch(`${API_BASE}/api${path}`, { ...fetchOptions, headers });
  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? body;
    } catch {
      /* ignore */
    }
    handleAuthenticationFailure(res.status, detail);
    const message = formatApiErrorDetail(detail, res.statusText);
    throw new ApiError(message, res.status, detail);
  }
  return res.json() as Promise<T>;
}

function formatApiErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (!item || typeof item !== 'object') return '';
        const row = item as { loc?: unknown[]; msg?: string; type?: string };
        const field = Array.isArray(row.loc)
          ? row.loc.filter((part) => part !== 'body' && part !== 'path').join('.')
          : '';
        const msg = row.msg || row.type || 'invalid';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const msg = (detail as { message: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) {
      return msg;
    }
  }
  if (detail && typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export const PLAN_WARNING_HEADER = 'X-Plan-Warning';

export type RequestWithWarningResult<T> = {
  data: T;
  planWarning: string | null;
};

export async function requestWithPlanWarning<T>(
  path: string,
  options: RequestOptions = {},
): Promise<RequestWithWarningResult<T>> {
  const { skipAuth: _skip, ...fetchOptions } = options;
  const headers = buildHeaders(options);

  const res = await fetch(`${API_BASE}/api${path}`, { ...fetchOptions, headers });
  if (res.status === 204) {
    return { data: undefined as T, planWarning: res.headers.get(PLAN_WARNING_HEADER) };
  }
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? body;
    } catch {
      /* ignore */
    }
    handleAuthenticationFailure(res.status, detail);
    const message =
      typeof detail === 'object' && detail !== null && 'message' in detail
        ? String((detail as { message: string }).message)
        : String(detail);
    throw new ApiError(message, res.status, detail);
  }
  const data = (await res.json()) as T;
  return { data, planWarning: res.headers.get(PLAN_WARNING_HEADER) };
}

export function healthCheck() {
  return request<{ status: string }>('/health', { skipAuth: true });
}
